/**
 * Employees React Query Hooks
 *
 * Custom hooks for employee CRUD operations using React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  employeesApi,
  niveausApi,
  uitDienstApi,
  type EmployeeFilters,
  type CreateEmployeeInput,
  type UpdateEmployeeInput,
  type SetUitDienstInput,
} from '../../lib/employeesApi';

// Query keys for cache management
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters?: EmployeeFilters) => [...employeeKeys.lists(), filters] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: number) => [...employeeKeys.details(), id] as const,
  teams: () => [...employeeKeys.all, 'teams'] as const,
};

export const niveauKeys = {
  all: ['niveaus'] as const,
  list: () => [...niveauKeys.all, 'list'] as const,
  detail: (id: number) => [...niveauKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch employee list with filters
 */
export function useEmployees(filters?: EmployeeFilters) {
  return useQuery({
    queryKey: employeeKeys.list(filters),
    queryFn: () => employeesApi.list(filters),
  });
}

/**
 * Hook to fetch single employee by ID
 */
export function useEmployee(id: number | undefined) {
  return useQuery({
    queryKey: employeeKeys.detail(id!),
    queryFn: () => employeesApi.get(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch unique teams
 */
export function useTeams() {
  return useQuery({
    queryKey: employeeKeys.teams(),
    queryFn: () => employeesApi.getTeams(),
  });
}

/**
 * Hook to fetch all niveaus
 */
export function useNiveaus() {
  return useQuery({
    queryKey: niveauKeys.list(),
    queryFn: () => niveausApi.list(),
    staleTime: 1000 * 60 * 60, // Cache niveaus for 1 hour (they rarely change)
  });
}

/**
 * Hook to create employee
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEmployeeInput) => employeesApi.create(input),
    onSuccess: () => {
      // Invalidate employee list cache
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.teams() });
    },
  });
}

/**
 * Hook to update employee
 */
export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateEmployeeInput }) =>
      employeesApi.update(id, input),
    onSuccess: (data, { id }) => {
      // Update cache with new data
      queryClient.setQueryData(employeeKeys.detail(id), data);
      // Invalidate list cache
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.teams() });
    },
  });
}

/**
 * Hook to delete employee
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => employeesApi.delete(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: employeeKeys.detail(id) });
      // Invalidate list cache
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.teams() });
    },
  });
}

/**
 * Hook to register an employee's uit-dienst-datum (Epic 5, Story 5.1/5.2,
 * AD-7). Invalidates both the detail (new status-chip/terugdraai-state) and
 * the list (medewerker verdwijnt uit de standaardlijst, FR-12) after
 * success -- same invalidation pattern as `useUpdateEmployee`.
 */
export function useSetUitDienst(employeeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetUitDienstInput) => uitDienstApi.setUitDienst(employeeId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(employeeKeys.detail(employeeId), data);
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}

/**
 * Hook to revert a previously registered uit-dienst-datum. Same
 * invalidation pattern as `useSetUitDienst` (symmetrical effect).
 */
export function useRevertUitDienst(employeeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => uitDienstApi.revertUitDienst(employeeId),
    onSuccess: (data) => {
      queryClient.setQueryData(employeeKeys.detail(employeeId), data);
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
}
